import { Head, Link } from "@inertiajs/react"
import {
  CheckCircledIcon,
  FileTextIcon,
  MagnifyingGlassIcon,
  PersonIcon,
  ReaderIcon
} from "@radix-ui/react-icons"

import flowcastMark from "@/assets/flowcast-mark.webp"
import { Button } from "@/components/ui/button"

const capabilities = [
  {
    icon: FileTextIcon,
    title: "Index policies and guidance",
    copy: "Upload PDFs and turn board materials, practice standards, benefits guides, and operating policies into searchable source material."
  },
  {
    icon: MagnifyingGlassIcon,
    title: "Answer member questions",
    copy: "Give association members a public or member-only chat experience grounded in your indexed documents and citations."
  },
  {
    icon: ReaderIcon,
    title: "Publish a document library",
    copy: "Expose every indexed document through an organization portal so members can browse, verify, and download the original PDFs."
  }
]

const portalRows = [
  { label: "Membership handbook", pages: "42 pages", status: "Indexed" },
  { label: "Practice standards", pages: "88 pages", status: "Indexed" },
  { label: "Benefits guide", pages: "31 pages", status: "Queued" }
]

function PortalPreview() {
  return (
    <div className="relative min-h-[28rem] overflow-hidden rounded-[1.75rem] border border-[var(--color-border)] bg-white/82 p-4 shadow-[var(--shadow-ambient)] backdrop-blur-xl">
      <div className="flex items-center justify-between gap-3 border-b border-[var(--color-border)] pb-4">
        <div className="flex items-center gap-3">
          <img alt="" className="h-10 w-10 rounded-xl" src={flowcastMark} />
          <div>
            <p className="text-sm font-semibold text-[var(--color-charcoal-blue)]">Association portal</p>
            <p className="text-xs text-[var(--color-text-faint)]">Public documents + member answers</p>
          </div>
        </div>
        <span className="rounded-full bg-[rgb(42_157_143/0.12)] px-3 py-1 text-xs font-semibold text-[var(--color-verdigris)]">
          Live
        </span>
      </div>

      <div className="mt-5 grid gap-4 lg:grid-cols-[1fr_0.8fr]">
        <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-panel)] p-4">
          <div className="flex items-center gap-2">
            <MagnifyingGlassIcon className="h-4 w-4 text-[var(--color-verdigris)]" />
            <p className="text-sm font-semibold text-[var(--color-charcoal-blue)]">Member question</p>
          </div>
          <p className="mt-4 rounded-lg bg-white p-3 text-sm leading-6 text-[var(--color-text-muted)] shadow-[var(--shadow-soft)]">
            What documents explain continuing education requirements for active members?
          </p>
          <div className="mt-4 rounded-lg border border-[var(--color-border)] bg-white p-3">
            <p className="text-sm leading-6 text-[var(--color-charcoal-blue)]">
              The current standard describes annual learning obligations and recordkeeping expectations for active members.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="rounded-full bg-[var(--color-panel)] px-3 py-1 text-xs font-medium text-[var(--color-text-muted)]">
                Practice standards, p. 12
              </span>
              <span className="rounded-full bg-[var(--color-panel)] px-3 py-1 text-xs font-medium text-[var(--color-text-muted)]">
                Membership handbook, p. 7
              </span>
            </div>
          </div>
        </section>

        <section className="rounded-xl border border-[var(--color-border)] bg-white p-4">
          <p className="text-sm font-semibold text-[var(--color-charcoal-blue)]">Indexed documents</p>
          <div className="mt-3 space-y-2">
            {portalRows.map((row) => (
              <div className="rounded-lg border border-[var(--color-border)] p-3" key={row.label}>
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm font-medium text-[var(--color-charcoal-blue)]">{row.label}</p>
                  <span className="rounded-full bg-[rgb(233_196_106/0.18)] px-2 py-0.5 text-[0.68rem] font-semibold text-[rgb(126_88_12)]">
                    {row.status}
                  </span>
                </div>
                <p className="mt-1 text-xs text-[var(--color-text-faint)]">{row.pages}</p>
              </div>
            ))}
          </div>
        </section>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        {["Cited answers", "Public library", "Org settings"].map((label) => (
          <div className="rounded-xl bg-[var(--color-charcoal-blue)] px-4 py-3 text-white" key={label}>
            <CheckCircledIcon className="h-4 w-4" />
            <p className="mt-2 text-sm font-medium">{label}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

function OrganizationList({ organizations = [] }) {
  if (organizations.length === 0) return null

  return (
    <section className="mx-auto max-w-7xl px-4 pb-10 sm:px-6 lg:px-8" id="your-portals">
      <div className="rounded-[1.75rem] border border-[var(--color-border)] bg-white/82 p-5 shadow-[var(--shadow-card)]">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-[0.65rem] font-semibold uppercase tracking-[0.24em] text-[var(--color-text-faint)]">
              Your portals
            </p>
            <h2 className="mt-2 text-2xl font-semibold text-[var(--color-charcoal-blue)]">
              Open an organization
            </h2>
          </div>
          <Button asChild type="button" variant="outline">
            <Link href="/orgs/new">Create organization</Link>
          </Button>
        </div>

        <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {organizations.map((organization) => (
            <Link
              className="rounded-xl border border-[var(--color-border)] bg-[var(--color-panel)] p-4 shadow-[var(--shadow-soft)] transition hover:bg-white"
              href={organization.href}
              key={organization.id}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="truncate text-base font-semibold text-[var(--color-charcoal-blue)]">{organization.name}</h3>
                  <p className="mt-1 text-xs text-[var(--color-text-faint)]">/orgs/{organization.slug}</p>
                </div>
                <span className="shrink-0 rounded-full bg-white px-3 py-1 text-xs font-medium capitalize text-[var(--color-text-muted)]">
                  {organization.membership_role}
                </span>
              </div>
              <p className="mt-4 text-sm font-medium text-[var(--color-verdigris)]">Open portal</p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  )
}

function PrimaryActionButton({ action }) {
  const href = action.href || "/login"
  const useAnchor = href.startsWith("#") || href === "/login" || href === "/create-account"
  const content = action.label

  return (
    <Button asChild type="button">
      {useAnchor ? <a href={href}>{content}</a> : <Link href={href}>{content}</Link>}
    </Button>
  )
}

function SignedInHome({ organizations = [] }) {
  return (
    <>
      <section className="mx-auto max-w-7xl px-4 pb-8 pt-8 sm:px-6 lg:px-8 lg:pt-14">
        <h1 className="max-w-4xl text-4xl font-semibold leading-tight text-[var(--color-charcoal-blue)] sm:text-5xl lg:text-6xl">
          Index your knowledge base for association members.
        </h1>
      </section>

      {organizations.length > 0 ? (
        <OrganizationList organizations={organizations} />
      ) : (
        <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
          <div className="rounded-[1.75rem] border border-[var(--color-border)] bg-white/82 p-6 shadow-[var(--shadow-card)]">
            <h2 className="text-2xl font-semibold text-[var(--color-charcoal-blue)]">Create your first organization</h2>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-[var(--color-text-muted)]">
              Set up an organization portal before uploading documents or opening member search.
            </p>
            <Button asChild className="mt-5" type="button">
              <Link href="/orgs/new">Create organization</Link>
            </Button>
          </div>
        </section>
      )}
    </>
  )
}

export default function HomePage({
  organizations = [],
  primary_action = { label: "Sign in", href: "/login" },
  signed_in = false
}) {
  return (
    <>
      <Head title="Knowledge base indexing for associations" />

      <main className="min-h-screen bg-[var(--color-app-bg)] text-[var(--color-text)]">
        <header className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-5 sm:px-6 lg:px-8">
          <Link className="flex items-center gap-3" href="/">
            <img alt="" className="h-9 w-9 rounded-xl" src={flowcastMark} />
            <span className="text-sm font-semibold uppercase tracking-[0.24em] text-[var(--color-charcoal-blue)]">
              Knowledge Viewer
            </span>
          </Link>

          <div className="flex items-center gap-3">
            {!signed_in ? (
              <Button asChild type="button" variant="ghost">
                <a href="/login">Sign in</a>
              </Button>
            ) : null}
            <PrimaryActionButton action={primary_action} />
          </div>
        </header>

        {signed_in ? (
          <SignedInHome organizations={organizations} />
        ) : (
          <>
            <section className="mx-auto grid max-w-7xl items-center gap-10 px-4 pb-12 pt-4 sm:px-6 lg:min-h-[calc(100vh-7rem)] lg:grid-cols-[0.9fr_1.1fr] lg:px-8 lg:pb-16">
              <div>
                <p className="inline-flex rounded-full border border-[var(--color-border)] bg-white/70 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--color-text-muted)] shadow-[var(--shadow-card)]">
                  For associations and member organizations
                </p>
                <h1 className="mt-6 max-w-4xl text-5xl font-semibold leading-[1.02] text-[var(--color-charcoal-blue)] sm:text-6xl lg:text-7xl">
                  Index your knowledge base for association members.
                </h1>
                <p className="mt-6 max-w-2xl text-lg leading-8 text-[var(--color-text-muted)]">
                  Create a branded organization portal where members can search official documents, ask cited questions, and open the original PDF sources.
                </p>

                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  <PrimaryActionButton action={primary_action} />
                  <Button asChild type="button" variant="outline">
                    <a href="/create-account">
                      <PersonIcon className="mr-2 h-4 w-4" />
                      Create account
                    </a>
                  </Button>
                </div>

                <div className="mt-10 grid gap-3 sm:grid-cols-3">
                  {["PDF ingestion", "Cited AI search", "Public document index"].map((label) => (
                    <div className="rounded-xl border border-[var(--color-border)] bg-white/72 px-4 py-3 shadow-[var(--shadow-card)]" key={label}>
                      <p className="text-sm font-semibold text-[var(--color-charcoal-blue)]">{label}</p>
                    </div>
                  ))}
                </div>
              </div>

              <PortalPreview />
            </section>

            <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
              <div className="grid gap-4 md:grid-cols-3">
                {capabilities.map((capability) => {
                  const Icon = capability.icon

                  return (
                    <article className="rounded-[1.5rem] border border-[var(--color-border)] bg-white/82 p-5 shadow-[var(--shadow-card)]" key={capability.title}>
                      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[var(--color-panel)] text-[var(--color-verdigris)]">
                        <Icon className="h-5 w-5" />
                      </div>
                      <h2 className="mt-4 text-lg font-semibold text-[var(--color-charcoal-blue)]">{capability.title}</h2>
                      <p className="mt-2 text-sm leading-6 text-[var(--color-text-muted)]">{capability.copy}</p>
                    </article>
                  )
                })}
              </div>
            </section>
          </>
        )}
      </main>
    </>
  )
}
